import { DataTable } from "@common/components/Table/DataTable";
import {
  ExpenseFromStipendExpensesSpendTableQuery,
  useStipendExpensesSpendTableQuery,
} from "./useStipendExpensesSpendTableQuery";
import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@common/components/Table/DataTableColumnHeader";
import { formatDate } from "date-fns";
import { formatDollars, formatPaymentMethod } from "@common/components/utils";
import { IconBox } from "@common/components";
import { Body } from "@hypercard/web-components";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AdminStipendExpenseFilterInput } from "@common/utils/graphql";
import { useMemo, useState } from "react";
import { PagePagination } from "@/components/ui/page-pagination";
import { getInitialsFromDisplayName } from "@/utils/utils";

// NOTE: make sure these two are in syncs
export enum StipendExpensesSpendTableColumnKeys {
  Merchant = "Merchant",
  Spender = "Spender",
  Date = "Date",
  PaymentMethod = "Payment Method",
  Amount = "Amount",
}
export const STIPEND_EXPENSES_SPEND_TABLE_COLUMN_OPTIONS = [
  StipendExpensesSpendTableColumnKeys.Spender,
  StipendExpensesSpendTableColumnKeys.Date,
  StipendExpensesSpendTableColumnKeys.PaymentMethod,
];

export const DEFAULT_STIPEND_EXPENSES_SPEND_TABLE_COLUMNS = [
  StipendExpensesSpendTableColumnKeys.Spender,
  StipendExpensesSpendTableColumnKeys.Date,
  StipendExpensesSpendTableColumnKeys.PaymentMethod,
];

const columns: ColumnDef<ExpenseFromStipendExpensesSpendTableQuery>[] = [
  {
    id: StipendExpensesSpendTableColumnKeys.Merchant,
    accessorKey: StipendExpensesSpendTableColumnKeys.Merchant,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="MERCHANT" />
    ),
    cell: ({ row }) => (
      <div className="flex items-center space-x-4">
        <IconBox className="h-10 w-10">
          <img src={row.original.stipendIconUrl} className="w-6" />
        </IconBox>
        <div className="space-y-1">
          <Body size="md" weight="regular">
            {row.original.merchantName}
          </Body>
          {row.original.user.employee?.workEmail && (
            <Body size="xs" weight="regular" className="text-white/70">
              {row.original.stipendName}
            </Body>
          )}
        </div>
      </div>
    ),
    // TODO: @rushil enable sorting
    enableSorting: false,
  },
  {
    id: StipendExpensesSpendTableColumnKeys.Spender,
    accessorKey: StipendExpensesSpendTableColumnKeys.Spender,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="SPENDER" />
    ),
    cell: ({ row }) => (
      <div className="flex items-center space-x-4">
        <Avatar className="h-8 w-8">
          <AvatarFallback>
            <Body size="sm" weight="regular">
              {getInitialsFromDisplayName(row.original.user.displayName)}
            </Body>
          </AvatarFallback>
        </Avatar>
        <div className="space-y-1">
          <Body size="md" weight="regular">
            {row.original.user.displayName}
          </Body>
          {row.original.user.employee?.workEmail && (
            <Body size="xs" weight="regular" className="text-white/70">
              {row.original.user.employee?.workEmail}
            </Body>
          )}
        </div>
      </div>
    ),
    enableSorting: false,
  },
  {
    id: StipendExpensesSpendTableColumnKeys.Date,
    accessorKey: StipendExpensesSpendTableColumnKeys.Date,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATE" />
    ),
    cell: ({ row }) => (
      <div>{formatDate(row.original.transactionDate, "MMM d, yyyy")}</div>
    ),
    enableSorting: false,
  },
  {
    id: StipendExpensesSpendTableColumnKeys.PaymentMethod,
    accessorKey: StipendExpensesSpendTableColumnKeys.PaymentMethod,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="PAYMENT METHOD" />
    ),
    cell: ({ row }) => (
      <div>{formatPaymentMethod(row.original.paymentMethod)}</div>
    ),
    enableSorting: false,
  },
  {
    id: StipendExpensesSpendTableColumnKeys.Amount,
    accessorKey: StipendExpensesSpendTableColumnKeys.Amount,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="AMOUNT"
        className="flex justify-end"
      />
    ),
    cell: ({ row }) => (
      <Body size="md" weight="regular" className="text-right">
        {formatDollars(row.original.amount)}
      </Body>
    ),
    enableSorting: false,
  },
];

type StipendExpensesSpendTableProps = {
  filters?: AdminStipendExpenseFilterInput;
  selectedColumns: StipendExpensesSpendTableColumnKeys[];
};

export const StipendExpensesSpendTable = ({
  filters,
  selectedColumns,
}: StipendExpensesSpendTableProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const { expenses, hasMorePages, loading, error } =
    useStipendExpensesSpendTableQuery({
      filters,
      currentPage,
    });

  const columnsToShow = useMemo(() => {
    const merchantColumn = columns.find(
      (column) => column.id === StipendExpensesSpendTableColumnKeys.Merchant,
    );
    const amountColumn = columns.find(
      (column) => column.id === StipendExpensesSpendTableColumnKeys.Amount,
    );
    const selectedColumnsFiltered = columns.filter((column) =>
      selectedColumns.includes(
        column.id as StipendExpensesSpendTableColumnKeys,
      ),
    );

    return [merchantColumn, ...selectedColumnsFiltered, amountColumn].filter(
      Boolean,
    ) as ColumnDef<ExpenseFromStipendExpensesSpendTableQuery>[];
  }, [selectedColumns]);

  // TODO: add loading and error states
  if (loading) {
    return null;
  } else if (error) {
    return null;
  }

  return (
    <div className="w-full">
      <DataTable columns={columnsToShow} data={expenses} />
      <PagePagination
        currentPage={currentPage}
        hasMorePages={hasMorePages}
        onChange={setCurrentPage}
      />
    </div>
  );
};
